import React, { FunctionComponent, memo, useEffect, useState } from 'react';
import styled from 'styled-components';
import { LocalizeContextProps, withLocalize } from 'react-localize-redux';
import useSWR from 'swr';

import { Text, ProgressStepper, useLocalize, Loading } from '@tradingblock/components';
import FundingMethods from './views/FundingMethods';
import FundingMethodSetupRequestStep from './views/FundingMethodSetupRequestStep';
import FundingRequestStep from './views/FundingRequestStep';
import { useApi } from '../../context';
import { useData } from '../../state/selectors';
import { DefaultApplicationModel } from '../../types';
import config from '../../config';
import {
  AccountStatus,
  AccountStatusesEnum,
  AchRelationship,
  AchRelationshipState,
  CashieringProfileState,
  Transfer,
  TransferDirections,
  TransferMechanisms,
  TransferState
} from '@tradingblock/types';
import { GetAccountDetailsUrl } from '@tradingblock/api';

export enum FundYourAccountViews {
  FundingMethods = 'FundingMethods',
  FundingMethodSetupRequestStep = 'FundingMethodSetupRequestStep',
  FundingRequestStep = 'FundingRequestStep',
};

export enum AchRelationshipFundingMethods {
  AchRelationshipViaPlaid = 'AchRelationshipViaPlaid',
  AchRelationshipViaMicroDeposits = 'AchRelationshipViaMicroDeposits',
};

export type AchRelationshipFundingMethod =
  | AchRelationshipFundingMethods.AchRelationshipViaMicroDeposits
  | AchRelationshipFundingMethods.AchRelationshipViaPlaid;

export type FundYourAccountView =
  | FundYourAccountViews.FundingMethods
  | FundYourAccountViews.FundingMethodSetupRequestStep
  | FundYourAccountViews.FundingRequestStep;

const StyledFundYourAccount = styled.div`
  h3 {
    margin-bottom: 1rem;
  }

  .loading-container {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  i.error, i.warning {
    font-size: 3em;
  }

  .congratulations, .cancel-request, .issue-on-our-end, .ineligible {
    margin-bottom: 0;
  }

  .warning {
    color: var(--yellow);
  }

  .ineligible, .issue-on-our-end {
    text-align: center;
  }
`;

const FundYourAccount: FunctionComponent<LocalizeContextProps> = memo(({ translate }) => {
  const [currentView, setCurrentView] = useState<FundYourAccountView>(FundYourAccountViews.FundingMethods);
  const [selectedAchRelationshipFundingMethod, setSelectedAchRelationshipFundingMethod] = useState<AchRelationshipFundingMethod | null>(null);
  const [transfers, setTransfers] = useState<Transfer[] | null>(null);
  const [cashieringProfileData, setCashieringProfileData] = useState<CashieringProfileState | null >(null);
  const [achRelationships, setAchRelationships] = useState<AchRelationship[] | null>(null);
  const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);
  const [workingOrCompletedAchRelationship, setWorkingOrCompletedAchRelationship] = useState<AchRelationship | null>(null);
  const [activeStepIndexOverride, setActiveStepIndexOverride] = useState<number | null>(null);
  const [workingOrCompletedAchTransfer, setWorkingOrCompletedAchTransfer] = useState<Transfer | null>(null);
  const [notEligibleToFundAccount, setNotEligibleToFundAccount] = useState<boolean>(false);

  const { getTranslationId } = useLocalize();

  const api = useApi();

  const application = useData(d => d.data.application);

  const { accountId } = application || DefaultApplicationModel;

  const {
    data: getAccountDetailsResponse,
  } = useSWR(
    accountId ? `${config.tradingApi}/${GetAccountDetailsUrl}?accountId=${accountId}` : null,
    () => {
      if (accountId) {
        return api.accounts.details(accountId);
      }

      return null;
    },
    { revalidateOnFocus: false, refreshInterval: 600000, shouldRetryOnError: false }
  )
  
  const {
    isLoading: isLoadingCashieringProfileData,
    data: getCashieringProfileResponse,
    error: errorGettingCashieringProfileData,
    isValidating: isValidatingCashieringProfileData,
  } = useSWR(
    accountId ? `${config.tradingApi}/Cashiering/${accountId}/Profile` : null,
    () => {
      if (accountId) {
        return api.cashiering.profile.get(accountId);
      }

      return null;
    },
    { revalidateOnFocus: false }
  );

  const {
    isLoading: isLoadingTransfersData,
    data: getTransfersResponse,
    error: errorGettingTransfersData,
    isValidating: isValidatingTransfersData,
    mutate: getTransfersData,
  } = useSWR(
    accountId ? `${config.tradingApi}/Cashiering/${accountId}/Transfers` : null,
    () => {
      if (accountId) {
        const workingAndCompleteTransferStates: TransferState[] = ['PendingRepCreateApproval', 'PendingFirmCreateApproval', 'Pending', 'Requested', 'FundsPosted', 'SentToBank', 'Postponed', 'Complete'];

        return api.cashiering.transfers.all(
          accountId,
          {
            states: workingAndCompleteTransferStates.join(),
            mechanisms: TransferMechanisms.Ach,
            directions: TransferDirections.Incoming
          }
        );
      }

      return null;
    },
    { revalidateOnFocus: false }
  );

  const {
    isLoading: isLoadingAchRelationshipsData,
    data: getAchRelationshipsResponse,
    error: errorGettingAchRelationshipsData,
    mutate: getAchRelationshipsData,
    isValidating: isValidatingAchRelationshipsData,
  } = useSWR(
    accountId ? `${config.tradingApi}/Cashiering/${accountId}/AchRelationships` : null,
    () => {
      if (accountId) {
        return api.cashiering.ach.all(accountId);
      }

      return null;
    },
    { revalidateOnFocus: false }
  );

  useEffect(() => {
    if (
      getCashieringProfileResponse &&
      getCashieringProfileResponse.responseCode === 0 &&
      getCashieringProfileResponse.payload
    ) {
      setCashieringProfileData(getCashieringProfileResponse.payload as CashieringProfileState);
    } else if (
      getCashieringProfileResponse === null ||
      (
        getCashieringProfileResponse &&
        (
          getCashieringProfileResponse.responseCode !== 0 ||
          !getCashieringProfileResponse.payload
        )
      )
    ) {
      setShowErrorMessage(true);
    }
  }, [getCashieringProfileResponse]);

  useEffect(() => {
    if (
      getTransfersResponse &&
      getTransfersResponse.responseCode === 0 &&
      getTransfersResponse.payload
    ) {
      setTransfers(getTransfersResponse.payload);
    } else if (
      getTransfersResponse === null ||
      (
        getTransfersResponse &&
        (
          getTransfersResponse.responseCode !== 0 ||
          !getTransfersResponse.payload
        )
      )
    ) {
      setShowErrorMessage(true);
    }
  }, [getTransfersResponse]);

  useEffect(() => {
    if (
      getAchRelationshipsResponse &&
      getAchRelationshipsResponse.responseCode === 0 &&
      getAchRelationshipsResponse.payload
    ) {
      setAchRelationships(getAchRelationshipsResponse.payload);
    } else if (
      getAchRelationshipsResponse === null ||
      (
        getAchRelationshipsResponse &&
        (
          getAchRelationshipsResponse.responseCode !== 0 ||
          !getAchRelationshipsResponse.payload
        )
      )
    ) {
      setShowErrorMessage(true)
    }
  }, [getAchRelationshipsResponse]);

  useEffect(() => {
    if (
      getAccountDetailsResponse &&
      getAccountDetailsResponse.responseCode === 0 &&
      getAccountDetailsResponse.payload
    ) {
      const accountStatus = getAccountDetailsResponse.payload.accountStatus as AccountStatus;

      if (
        accountStatus === AccountStatusesEnum.Closed ||
        accountStatus === AccountStatusesEnum.ClosedNeverFunded ||
        accountStatus === AccountStatusesEnum.Rejected
      ) {
        setNotEligibleToFundAccount(true);
      } else {
        setNotEligibleToFundAccount(false);
      }
    }
  }, [getAccountDetailsResponse]);

  useEffect(() => {
    if (errorGettingAchRelationshipsData || errorGettingCashieringProfileData || errorGettingTransfersData) {
      setShowErrorMessage(true);
    } else {
      setShowErrorMessage(false);
    }
  }, [errorGettingAchRelationshipsData, errorGettingCashieringProfileData, errorGettingTransfersData]);

  useEffect(() => {
    if (transfers && achRelationships) {
      if (transfers.length > 0) {
          setWorkingOrCompletedAchTransfer(transfers[0]);
      }

      if (achRelationships.length > 0) {
        const workingAndCompleteAchRelationshipStates: AchRelationshipState[] = ['PendingCreateApproval', 'Pending', 'Approved']; 

        const firstWorkingOrCompleteAchRelationship = achRelationships.find((achRelationship) => workingAndCompleteAchRelationshipStates.includes(achRelationship.state));

        if (firstWorkingOrCompleteAchRelationship) {
          setWorkingOrCompletedAchRelationship(firstWorkingOrCompleteAchRelationship);
        }
      }
    }
  }, [transfers, achRelationships]);

  if (notEligibleToFundAccount) {
    return (
      <StyledFundYourAccount>
        <h3>
          <Text id="fundYourAccountComponent.fundYourAccount" />
        </h3>

        <div className="d-flex justify-content-center align-items-center flex-direction-col">
          <i className="fal fa-exclamation-circle warning m-1" />
          <p className="ineligible">
            <Text id="fundYourAccountComponent.notEligibleToFundAccount" type="error" />
          </p>
        </div>
      </StyledFundYourAccount>
    );
  }

  return (
    <StyledFundYourAccount>
      <h3>
        <Text id="fundYourAccountComponent.fundYourAccount" />
      </h3>

      {
        isLoadingAchRelationshipsData ||
        isLoadingTransfersData ||
        isLoadingCashieringProfileData ||
        isValidatingAchRelationshipsData ||
        isValidatingCashieringProfileData ||
        isValidatingTransfersData &&
        <div className="loading-container">
          <Loading />
        </div>
      }

      {
        showErrorMessage &&
        !isLoadingAchRelationshipsData &&
        !isLoadingCashieringProfileData &&
        !isLoadingTransfersData &&
        !isValidatingAchRelationshipsData &&
        !isValidatingCashieringProfileData &&
        !isValidatingTransfersData &&
        (
          <div className="d-flex justify-content-center align-items-center flex-direction-col">
            <i className="fal fa-exclamation-circle error m-1" />
            <p className="issue-on-our-end">
              <Text id="fundYourAccountComponent.issueOnOurEnd" type="error" />
            </p>
          </div>
        )
      }

      {
        !showErrorMessage &&
        !isLoadingAchRelationshipsData &&
        !isLoadingCashieringProfileData &&
        !isLoadingTransfersData &&
        !isValidatingAchRelationshipsData &&
        !isValidatingCashieringProfileData &&
        !isValidatingTransfersData &&
        currentView !== FundYourAccountViews.FundingMethods &&
        !workingOrCompletedAchTransfer &&
        <ProgressStepper
          activeStepIndex={activeStepIndexOverride || Object.keys(FundYourAccountViews).indexOf(currentView) - 1}
          stepLabels={[
            translate(getTranslationId({ id: 'fundYourAccountComponent.fundingMethodSetupRequest' })) as string,
            translate(getTranslationId({ id: 'fundYourAccountComponent.fundingRequest' })) as string
          ]}
        />
      }

      {
        currentView === FundYourAccountViews.FundingMethods &&
        cashieringProfileData &&
        !showErrorMessage &&
        !isLoadingAchRelationshipsData &&
        !isLoadingCashieringProfileData &&
        !isLoadingTransfersData &&
        !isValidatingAchRelationshipsData &&
        !isValidatingCashieringProfileData &&
        !isValidatingTransfersData &&
        <FundingMethods
          setCurrentFundYourAccountView={setCurrentView}
          setSelectedAchRelationshipFundingMethod={setSelectedAchRelationshipFundingMethod}
          transfersDataFromCashieringProfile={cashieringProfileData.transfers}
          inProgressFundingRequestViaLinkedBankAccount={!!workingOrCompletedAchRelationship}
          submittedFundingRequestViaLinkedBankAccount={!!workingOrCompletedAchTransfer}
        />
      }

      {
        currentView === FundYourAccountViews.FundingMethodSetupRequestStep &&
        !showErrorMessage &&
        !isLoadingAchRelationshipsData &&
        !isLoadingCashieringProfileData &&
        !isLoadingTransfersData &&
        !isValidatingAchRelationshipsData &&
        !isValidatingCashieringProfileData &&
        !isValidatingTransfersData &&
        <FundingMethodSetupRequestStep
          setCurrentFundYourAccountView={setCurrentView}
          selectedAchRelationshipFundingMethod={selectedAchRelationshipFundingMethod}
          workingOrCompletedAchRelationship={workingOrCompletedAchRelationship}
          getAchRelationshipsData={getAchRelationshipsData}
        />
      }

      {
        currentView === FundYourAccountViews.FundingRequestStep &&
        cashieringProfileData &&
        workingOrCompletedAchRelationship &&
        !showErrorMessage &&
        !isLoadingAchRelationshipsData &&
        !isLoadingCashieringProfileData &&
        !isLoadingTransfersData &&
        !isValidatingAchRelationshipsData &&
        !isValidatingCashieringProfileData &&
        !isValidatingTransfersData &&
        <FundingRequestStep
          iraConstraints={cashieringProfileData.iraConstraints}
          achRelationship={workingOrCompletedAchRelationship}
          setCurrentFundYourAccountView={setCurrentView}
          setActiveStepIndexOverride={setActiveStepIndexOverride}
          workingOrCompletedAchTransfer={workingOrCompletedAchTransfer}
          getTransfersData={getTransfersData}
        />
      }
    </StyledFundYourAccount>
  );
});

export default withLocalize(FundYourAccount);